import { useEffect, useRef, useState } from 'react'
import { Input, Label, Textarea } from '@quickmd-llc/design-system'
import type { UseFormRegister, Control, FieldErrors, UseFormSetValue } from 'react-hook-form'
import { useController } from 'react-hook-form'
import type { MedicationFormValues } from '../../types'
import { MedicationImageUpload } from './MedicationImageUpload'
import { FormSectionCard } from './FormSectionCard'

interface MedicationInfoSectionProps {
  register: UseFormRegister<MedicationFormValues>
  control: Control<MedicationFormValues>
  errors: FieldErrors<MedicationFormValues>
  setValue: UseFormSetValue<MedicationFormValues>
  showIndicator: boolean
  isValid: boolean
  existingImageUrl?: string | null
  touchedFields: Partial<Record<keyof MedicationFormValues, unknown>>
  isSubmitted: boolean
}

export function MedicationInfoSection({
  register,
  control,
  errors,
  setValue,
  showIndicator,
  isValid,
  existingImageUrl,
  touchedFields,
  isSubmitted,
}: MedicationInfoSectionProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(existingImageUrl ?? null)
  const objectUrlRef = useRef<string | null>(null)

  const { field: imageField } = useController({ name: 'medicationImage', control })

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }
  }, [])

  function handleImageChange(file: File) {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    const url = URL.createObjectURL(file)
    objectUrlRef.current = url
    setPreviewUrl(url)
    setValue('medicationImage', file)
    imageField.onChange(file)
  }

  const showDrugNameError = (touchedFields.drugName || isSubmitted) && errors.drugName
  const showImageError = isSubmitted && errors.medicationImage

  return (
    <FormSectionCard title='Medication information' showIndicator={showIndicator} isValid={isValid}>
      <div className='flex gap-sp-2'>
        <div>
          <MedicationImageUpload
            value={imageField.value}
            previewUrl={previewUrl}
            onChange={handleImageChange}
            hasError={Boolean(showImageError)}
          />
          {showImageError ?
            <Label size='sm' className='mt-sp-0.5 block text-center text-text-error'>
              {errors.medicationImage?.message}
            </Label>
          : null}
        </div>

        <div className='flex flex-1 flex-col gap-sp-2'>
          <div>
            <Label size='lg' className='mb-sp-0.5 block'>
              <label htmlFor='drugName'>Medication name</label>
            </Label>
            <Input
              id='drugName'
              {...register('drugName')}
              placeholder='e.g. Oral Estradiol 0.5 mg'
              error={Boolean(showDrugNameError)}
            />
            {showDrugNameError ?
              <Label size='sm' className='mt-sp-0.5 text-text-error'>
                {errors.drugName?.message}
              </Label>
            : null}
          </div>

          <div>
            <Label size='lg' className='mb-sp-0.5 block'>
              <label htmlFor='displayDescription'>Patient-facing description</label>
            </Label>
            <Textarea
              id='displayDescription'
              {...register('displayDescription')}
              rows={4}
              placeholder='Patient-facing instructions for this medication'
            />
          </div>
        </div>
      </div>
    </FormSectionCard>
  )
}
